import React from 'react';
import Parse from 'parse';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import IngestModal from '../ui/ingest.component';
import AIChatBot from '../ui/widgets/aiChatbot.component';

export default function LifeBotSetup({
  lifeObjectId,
  configuration,
  onChange,
  ensureLifeObject,
  onBusyChange,
}) {
  const [error, setError] = React.useState('');
  const [testing, setTesting] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const refresh = async () => {
    setRefreshing(true);
    try {
      const object = await Parse.Cloud.run('getLifeObject', {
        id: lifeObjectId,
      });
      onChange(object.chatbot || {});
      setError('');
    } catch (e) {
      setError(e.message || 'Could not check your LifeBot.');
    } finally {
      setRefreshing(false);
    }
  };
  React.useEffect(() => {
    if (configuration?.status !== 'processing' || !lifeObjectId)
      return undefined;
    let active = true;
    const poll = async () => {
      try {
        const object = await Parse.Cloud.run('getLifeObject', {
          id: lifeObjectId,
        });
        if (active) onChange(object.chatbot || {});
      } catch (e) {
        if (active) setError(e.message || 'Could not check processing status.');
      }
    };
    const timer = setInterval(poll, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [lifeObjectId, configuration?.status]); // onChange updates the current draft.
  return (
    <Stack spacing={2} className="lt-lifebot-studio">
      <Typography variant="h5">Build your LifeBot</Typography>
      <Typography>
        Connect your tools, add knowledge, and test an interactive experience
        before publishing.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {configuration?.status === 'processing' && (
        <Alert icon={<CircularProgress size={18} />} severity="info">
          Your LifeBot is learning from its sources. You can leave this screen
          and return.
        </Alert>
      )}
      {configuration?.status === 'failed' && (
        <Alert severity="error">
          Your sources could not be processed. Review them below and try again.
        </Alert>
      )}
      {configuration?.status === 'ready' && (
        <Alert severity="success">Your LifeBot is ready to test.</Alert>
      )}
      <Stack direction="row" spacing={1}>
        <Button onClick={refresh} disabled={refreshing || !lifeObjectId}>
          Check status
        </Button>
        <Button
          variant="contained"
          disabled={configuration?.status !== 'ready'}
          onClick={() => setTesting(!testing)}
        >
          {testing ? 'Back to sources' : 'Test LifeBot'}
        </Button>
      </Stack>
      {testing ? (
        <AIChatBot
          chatbotId={configuration.chatBotId}
          preText={configuration.initialPrompt}
          embedded
        />
      ) : (
        <Box
          sx={{
            pointerEvents:
              submitting || configuration?.status === 'processing'
                ? 'none'
                : 'auto',
            opacity: submitting ? 0.6 : 1,
          }}
        >
          <IngestModal
            onBusyChange={onBusyChange}
            chatBot={configuration}
            onIngestSuccess={async (params) => {
              setSubmitting(true);
              setError('');
              try {
                const id =
                  lifeObjectId || (await ensureLifeObject(params.botName));
                const result = await Parse.Cloud.run('configureLifeBot', {
                  ...params,
                  lifeObjectId: id,
                });
                onChange(result);
              } catch (e) {
                setError(e.message || 'Could not create your LifeBot.');
                throw e;
              } finally {
                setSubmitting(false);
              }
            }}
          />
        </Box>
      )}
    </Stack>
  );
}
